import { lazy } from "react";
import { Navigate } from "react-router-dom";
import Loadable from "../layout/fullLayout/loadable/loadable";
import { OOPSError } from "../views/authentication/OopsError";
import { ProtectedRoute } from "./ProtectedRoutes";
import { PublicRoute } from "./PublicRoute";
import { customerRoutes } from "../views/customers/customer.router";
import { leadRoutes } from "../views/leads/Leads.router";
import { opportunityRoutes } from "../views/opportunites/opportunity.router";
import { profileRoutes } from "../views/Profile/profile.router";
import { securityRoutes } from "../views/Security/security.router";
import { templateRoutes } from "../views/EmailTemplates/EmailTemplate.router";
import { dashboardRoutes } from "../views/dashboard/dashboard.router";
import { envConfigRoutes } from "../views/EnvConfig/EnvConfig.router";
import { Chat } from "../views/Chat";
import { componyInformationRoutes } from "../views/CompanyInformation/CompanyInformation.router";
import { inventoryRoutes } from "../views/Inventory/router";
import { categoryRoutes } from "../views/category/category.router";
import { bookingRoutes } from "../views/bookingList/booking.router";
import { categoryTypeRoutes } from "../views/categoryType/categoryType.router";
import { foodMenuRoutes } from "../views/Food/food.router";
import { ReviewReplyRoutes } from "../views/ReviewReply/ReviewReply.router";
import { marketCampaign } from "../views/WhatsAppTemplate/whatsappTemplate.router";
import { zoneRoutes } from "../views/userZone/userZone.router";
import { whatsAppQRRoutes } from "../views/whatsAppQR/whatsAppQR.router";
import { orgEnvConfigRoutes } from "../views/orgEnvConfig/orgEnvConfig.router";
import { CompanyOutletRoutes } from "../views/companyOutlet/companyOutlet.router";
import { paymentDetailsRoutes } from "../views/paymentDetails/paymentDetails.router";
import { OtherUserLogin } from "../views/authentication/OtherUserLogin";
import { SubscriptionRoutes } from "../views/subscription/subscription.router";
import { ChooseSubscription } from "../views/chooseSubscription/chooseSubscription";
import { automationListRoutes } from "../views/Automation/Automation.router";
import { StatusRoutes } from "../views/status/status.router";
import { CustomCancelationReasonRoutes } from "../views/customCancelationReason/CustomCancelationReason.route";
import { enrCancelationReasonRoutes } from "../views/enrCancelationReason/enrCancelationReason.route";
import { chatbotRoutes } from "../views/Chatbot/chatbot.router";
import { marketCampaignV2Routes } from "../views/market-campaign/MarketCampaign.router";
import { LandingPage } from "../views/authentication/SignUp/LandingPage";
import { UserSignUp } from "../views/authentication/SignUp/UserSignUp";
import { supportRoutes } from "../views/support/support.router";
import { masterFormsRoutes } from "../views/MasterForms/masterForms.router";
import { serviceRoutes } from "../views/service/service.router";
import { eventsRoutes } from "../views/events/events.router";
import { dynamicFormsRoutesV2 } from "../views/DynamicFormsV2/dynamicForms.router";
import { upsellRoutes } from "../views/upselling/upselling.router";
import { genAiRoutes } from "../views/GenAI/genAi.router";
import { chatLeadRoutes } from "../views/chatLeads/chatLeads.router";
import { ChooseSubscriptionRoutes } from "../views/chooseSubscription/chooseSubscription.router";
import { quickReplyTemplateRoutes } from "../views/QuickReplyTemplate/quickReplyTemplate.router";
import { CompanyNotAssign } from "../views/authentication/CompanyNotAssign";
import { RestrictedWordsRoutes } from "../views/restrictedWords/restrictedWords.router";
import { totalBookingRoutes } from "../views/totalBookings/totalbooking.router";
/* ***Layouts**** */
/**
 * Lazy loads the FullLayout component.
 *
 * This approach is used to split the code and load the FullLayout component only when it is needed.
 * It helps in reducing the initial load time of the application by deferring the loading of the FullLayout component
 * until it is actually required. This can improve the performance and user experience of the application.
 *
 * The `Loadable` function is used to handle the loading state and potential errors during the loading process.
 * The `lazy` function from React is used to dynamically import the FullLayout component.
 */

const FullLayout = Loadable(
  lazy(() =>
    import("../layout/fullLayout/FullLayout").then(({ FullLayout }) => ({
      default: FullLayout,
    }))
  )
);
const BlankLayout = Loadable(
  lazy(() => import("../layout/blankLayout/BlankLayout"))
);
/* ***End Layouts**** */

const Error = Loadable(
  lazy(() =>
    import("../views/authentication/Error").then(({ Error }) => ({
      default: Error,
    }))
  )
);

/* ****Pages***** */

const Login = Loadable(
  lazy(() =>
    import("../views/authentication/Login").then(({ Login }) => ({
      default: Login,
    }))
  )
);
const AccessDenied = Loadable(
  lazy(() =>
    import("../views/authentication/AccessDenied").then(({ AccessDenied }) => ({
      default: AccessDenied,
    }))
  )
);

const SubscribeToAccess = Loadable(
  lazy(() =>
    import("../views/authentication/SubscribeToAccess").then(
      ({ SubscribeToAccess }) => ({
        default: SubscribeToAccess,
      })
    )
  )
);

const SubscriptionError = Loadable(
  lazy(() =>
    import("../views/authentication/SubscriptionError").then(
      ({ SubscriptionError }) => ({
        default: SubscriptionError,
      })
    )
  )
);

const PublicManageLead = Loadable(
  lazy(() =>
    import("../views/PublicLead/publicManageLead").then(
      ({ PublicManageLead }) => ({
        default: PublicManageLead,
      })
    )
  )
);

const ForgotPassword = Loadable(
  lazy(() =>
    import("../views/authentication/ForgotPassword").then(
      ({ ForgotPassword }) => ({
        default: ForgotPassword,
      })
    )
  )
);

export const Router = [
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <FullLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: "/",
        exact: true,
        element: <Navigate to="/dashboard" replace />,
      },

      // ------------- opportunites ----------------------------

      { path: "access", element: <AccessDenied /> },
      { path: "subscribe-required", element: <SubscribeToAccess /> },
      { path: "error", element: <OOPSError /> },
      { path: "*", element: <Navigate to="/auth/404" /> },
    ]
      .concat(dashboardRoutes)
      .concat(leadRoutes)
      .concat(chatbotRoutes)
      .concat(customerRoutes)
      .concat(opportunityRoutes)
      .concat(profileRoutes)
      .concat(securityRoutes)
      .concat(templateRoutes)
      .concat(envConfigRoutes)
      .concat(componyInformationRoutes)
      .concat(inventoryRoutes)
      .concat(categoryRoutes)
      .concat(bookingRoutes)
      .concat(supportRoutes)
      .concat(masterFormsRoutes)
      .concat(dynamicFormsRoutesV2)
      .concat(serviceRoutes)
      .concat(eventsRoutes)
      .concat(categoryTypeRoutes)
      .concat(foodMenuRoutes)
      .concat(ReviewReplyRoutes)
      .concat(marketCampaign)
      .concat(zoneRoutes)
      .concat(whatsAppQRRoutes)
      .concat(CompanyOutletRoutes)
      .concat(paymentDetailsRoutes)
      .concat(SubscriptionRoutes)
      .concat(orgEnvConfigRoutes)
      .concat(automationListRoutes)
      .concat(StatusRoutes)
      .concat(CustomCancelationReasonRoutes)
      .concat(enrCancelationReasonRoutes)
      .concat(marketCampaignV2Routes)
      .concat(genAiRoutes)
      .concat(upsellRoutes)
      .concat(chatLeadRoutes)
      .concat(ChooseSubscriptionRoutes)
      .concat(quickReplyTemplateRoutes)
      .concat(RestrictedWordsRoutes)
      .concat(totalBookingRoutes),
  },

  // --------------------------------TASK ROUTING----------------------------------------------

  {
    path: "/auth",
    element: (
      <PublicRoute>
        <BlankLayout />
      </PublicRoute>
    ),
    children: [
      { path: "404", element: <Error /> },
      { path: "login", element: <Login /> },
      { path: "reset-password", element: <ForgotPassword /> },
      // start prod
      { path: "signup", element: <LandingPage /> },
      { path: "signup/setup-company", element: <UserSignUp /> },
      // end prod
      { path: "*", element: <Navigate to="/auth/404" /> },
    ],
  },
  {
    path: "/other",
    element: <BlankLayout />,
    children: [
      { path: "user-access", element: <OtherUserLogin /> },
      { path: "404", element: <Error /> },
      { path: "login", element: <Login /> },
      { path: "reset-password", element: <ForgotPassword /> },
      { path: "*", element: <Navigate to="/auth/404" /> },
    ],
  },
  // start prod
  {
    path: "/choose-subscription",
    element: <ChooseSubscription />,
  },
  // end prod
  {
    path: "/subscription-error",
    element: <SubscriptionError />,
  },
  {
    path: "/company-not-assigned",
    element: <CompanyNotAssign />,
  },
  {
    path: "/student-lead-form",
    element: <PublicManageLead />,
  },
  { path: "/chat", element: <Chat /> },
];
